import { FileDownloadOutlined } from "@mui/icons-material";
import { Box, Button, Stack, Typography, useTheme } from "@mui/material";
import React, { useMemo } from "react";
import { DatePickerButton } from "../../../components/DatePickerButton";
import { useLocalization } from "../../../hooks/useLocalization";
import { useAuditLogExport } from "../hooks/useAuditLogExport";
import { formatDate } from "../utils/csvAndDateUtils";
import { CalendarPopover } from "./CalendarPopover";

type Props = {
  user: { companyId?: string; userId?: string; roles?: string[] } | undefined;
  company: unknown;
};

export const AuditLogExportSection: React.FC<Props> = ({ user, company }) => {
  const { t } = useLocalization();
  const theme = useTheme();
  const {
    fromDate,
    toDate,
    openWhich,
    canExport,
    fromBtnRef,
    toBtnRef,
    calendarRef,
    setFromDate,
    setToDate,
    setOpenWhich,
    handleFromClick,
    handleToClick,
    handleExport,
    today,
    minFrom,
  } = useAuditLogExport(user, company);

  const maxFrom = useMemo(() => {
    if (!toDate) return today;

    return toDate.getTime() < today.getTime() ? toDate : today;
  }, [toDate, today]);

  const maxTo = today;
  const minTo = fromDate ?? null;

  const Description = (
    <Typography
      sx={{
        fontWeight: 400,
        fontSize: 12,
        lineHeight: "16px",
        letterSpacing: "0.02em",
        color: "#636366",
      }}
    >
      {t("company.auditLogExport.description")}
    </Typography>
  );

  const renderExportBtn = () => (
    <Button
      disabled={!canExport}
      onClick={() => void handleExport()}
      startIcon={<FileDownloadOutlined />}
      sx={{
        height: 56,
        width: 152,
        borderRadius: "16px",
        mt: 2,
        textTransform: "none",
        fontSize: 14,
        fontWeight: 600,
        lineHeight: "20px",
        letterSpacing: "0.02em",
        bgcolor: "linear-gradient(180deg, #288BFE 0%, #0C6FE2 100%)",
        color: "#FFFFFF",
        boxShadow: "none",
        "&.Mui-disabled": {
          bgcolor: "linear-gradient(180deg, #288BFE 0%, #0C6FE2 100%)",
          color: "#FFFFFF",
          opacity: 0.4,
        },
      }}
      variant="contained"
    >
      {t("company.auditLogExport.exportButton")}
    </Button>
  );

  return (
    <Stack spacing={2} sx={{ width: "100%", minWidth: 0 }}>
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: 18,
          lineHeight: "24px",
          letterSpacing: "0.02em",
          color: theme.colors.text[5] || "#1C1C1E",
          pt: 2,
        }}
      >
        {t("company.auditLogExport.title")}
      </Typography>

      {Description}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
          columnGap: { xs: 0, md: 2 },
          rowGap: 2,
          alignItems: "end",
          maxWidth: { xs: "100%", md: "680px" },
        }}
      >
        {/* From button */}
        <Box sx={{ gridColumn: { xs: "1 / -1", md: "auto" } }}>
          <DatePickerButton
            buttonRef={fromBtnRef}
            formatDate={formatDate}
            label={t("company.auditLogExport.fromLabel")}
            onClick={handleFromClick}
            placeholder={t("company.auditLogExport.fromPlaceholder")}
            value={fromDate}
          />
        </Box>

        {/* To button */}
        <Box sx={{ gridColumn: { xs: "1 / -1", md: "auto" } }}>
          <DatePickerButton
            buttonRef={toBtnRef}
            disabled={!fromDate}
            formatDate={formatDate}
            label={t("company.auditLogExport.toLabel")}
            onClick={handleToClick}
            placeholder={t("company.auditLogExport.toPlaceholder")}
            value={toDate}
          />
        </Box>

        {/* Export button */}
        <Box
          sx={{
            gridColumn: { xs: "1 / -1", md: "2" },
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          {renderExportBtn()}
        </Box>
      </Box>

      {/* Calendar popovers */}
      <CalendarPopover
        anchorEl={fromBtnRef.current}
        calendarRef={calendarRef}
        enableFuture={false}
        maxDate={maxFrom}
        minDate={minFrom}
        onApply={(d) => setFromDate(d)}
        onClose={() => setOpenWhich(null)}
        open={openWhich === "from"}
        value={fromDate}
      />
      <CalendarPopover
        anchorEl={toBtnRef.current}
        calendarRef={calendarRef}
        enableFuture={false}
        maxDate={maxTo}
        minDate={minTo}
        onApply={(d) => setToDate(d)}
        onClose={() => setOpenWhich(null)}
        open={openWhich === "to"}
        value={toDate}
      />
    </Stack>
  );
};
